<div class="event-creation-wrapper">
  <!-- Enhanced Header Section -->
  <div class="template-header">
    <div class="header-content">
      <h1 class="template-title">Event Creation</h1>
      <p class="template-description">
        This is the web based version of the <code>/event create</code> command.
      </p>
      <div class="creation-mode-toggle" *ngIf="guild">
        <button type="button" class="btn btn-mode"
                [class.btn-mode-active]="creationMode === 'simple'"
                (click)="setCreationMode('simple')">Simple</button>
        <button type="button" class="btn btn-mode"
                [class.btn-mode-active]="creationMode === 'advanced'"
                (click)="setCreationMode('advanced')">Advanced</button>
      </div>
    </div>
  </div>

  <!-- Loading State -->
  <div *ngIf="!guild" class="loading-state">
    <mat-spinner diameter="40"></mat-spinner>
    <p>Loading...</p>
  </div>

  <!-- Main Content Grid -->
  <div class="row g-4" *ngIf="guild">
    <!-- Event Setup Column -->
    <div class="col-12 col-xxl settings-column">
      <div class="section-header">
        <h2 class="section-title">Event Setup</h2>
        <mat-icon class="section-icon">event</mat-icon>
      </div>
      <!-- Select channel and event template -->
      <form [formGroup]="eventForm" (ngSubmit)="onSubmit()">
      <div class="row pe-3">
        <div class="col-12 col-md-6 pe-0">
          <mat-form-field class="full-width-container">
            <mat-label>Event/LFG Channel </mat-label>
            <mat-select id="eventChannel" name="eventChannel" formControlName="eventChannel">
              <ng-container *ngFor="let channel of allowedChannels">
                <mat-option [value]="channel.id">
                  {{ channel.name }}
                </mat-option>
              </ng-container>
            </mat-select>
          </mat-form-field>
        </div>
        <div class="col-12 col-md-6 pe-0" *ngIf="creationMode === 'advanced'">
          <mat-form-field class="full-width-container">
            <mat-label>Event Template</mat-label>
            <mat-select id="eventTemplate" name="eventTemplate" formControlName="eventTemplate">
              <ng-container *ngFor="let template of eventTemplates">
                <mat-option [value]="template.e_identifier">
                  {{ template.title }}
                </mat-option>
              </ng-container>
            </mat-select>
          </mat-form-field>
        </div>
      </div>
      <div class="row pe-3" *ngIf="creationMode === 'advanced'">
        <div class="col-12 col-md-6 pe-0">
          <mat-form-field class="full-width-container">
            <mat-label>Signup Group (Premium)</mat-label>
            <mat-select formControlName="signupGroup" [disabled]="!guild?.has_premium">
              <mat-option *ngFor="let group of filteredSignupGroups" [value]="group.identifier">
                {{ group.signupGroupTitle }}
              </mat-option>
              <mat-option *ngIf="filteredSignupGroups.length === 0" disabled>
                No signup group for the selected signup template
              </mat-option>
            </mat-select>
            <mat-hint *ngIf="!guild?.has_premium">Requires Premium
            </mat-hint>
          </mat-form-field>
        </div>
      </div>
      
      <!-- Event Details Section -->
      <div class="section-header mt-4">
        <h2 class="section-title">Event Details</h2>
        <mat-icon class="section-icon">edit</mat-icon>
      </div>
      <!-- Title -->
      <mat-form-field class="full-width-container">
        <mat-label>Title </mat-label>
        <input matInput formControlName="title">
        <mat-error *ngIf="eventForm.get('title').hasError('required')">
          Title is required
        </mat-error>
        <mat-error *ngIf="eventForm.get('title').errors?.maxlength">Title is too long (max.
          60)
        </mat-error>
        <mat-hint align="end">{{eventForm.get('title').value.length}} / 60</mat-hint>
      </mat-form-field>

      <div class="row pe-3">
        <!-- Start Date and Time -->
        <div class="col-12 col-md-6 pe-0">
          <mat-form-field class="full-width-container">
            <mat-label>Start Date and Time </mat-label>
            <input matInput [ngxMatDatetimePicker]="startPicker" placeholder="Choose a date" formControlName="start"
              [min]="startMinDate" [max]="maxDate">
            <mat-datepicker-toggle matSuffix [for]="startPicker"></mat-datepicker-toggle>
            <ngx-mat-datetime-picker #startPicker [touchUi]="touchUi" [enableMeridian]="enableMeridian"
              [showSeconds]="false">
            </ngx-mat-datetime-picker>
            <mat-error *ngIf="eventForm.get('start').hasError('required')">
              Start Date and Time is required
            </mat-error>
          </mat-form-field>
        </div>
        <!-- End Date and Time -->
        <div class="col-12 col-md-6 pe-0">
          <mat-form-field class="full-width-container">
            <mat-label>End Date and Time</mat-label>
            <input matInput [ngxMatDatetimePicker]="endPicker" placeholder="Choose a date" formControlName="end"
              [min]="endMinDateIso" [max]="maxDate">
            <mat-datepicker-toggle matSuffix [for]="endPicker"></mat-datepicker-toggle>
            <ngx-mat-datetime-picker #endPicker [touchUi]="touchUi" [enableMeridian]="enableMeridian">
            </ngx-mat-datetime-picker>
            <mat-error *ngIf="eventForm.get('end').hasError('matDatetimePickerMin')">
              End Date / Time needs to be after Start Date / Time
            </mat-error>
          </mat-form-field>
        </div>
      </div>

      <!-- Description (Advanced only) -->
      <mat-form-field class="full-width-container" *ngIf="creationMode === 'advanced'">
        <mat-label>Description</mat-label>
        <textarea matInput formControlName="description"></textarea>
        <mat-error *ngIf="eventForm.get('description').errors?.maxlength">Description is too long (max.
          700)
        </mat-error>
        <mat-hint align="end">{{eventForm.get('description').value.length}} / 700</mat-hint>
      </mat-form-field>

      <!-- Signup Template -->
      <mat-form-field class="full-width-container">
        <mat-label>Signup Template </mat-label>
        <mat-select id="signupTemplate" formControlName="signupTemplate">
          <ng-container *ngFor="let tempalte of signupTemplates">
            <mat-option [value]="tempalte.identifier">
              {{ tempalte.name }}
            </mat-option>
          </ng-container>
        </mat-select>
      </mat-form-field>

      <!-- Advanced-only fields -->
      <ng-container *ngIf="creationMode === 'advanced'">
      <mat-form-field class="full-width-container">
        <mat-label>Text</mat-label>
        <textarea matInput formControlName="text"></textarea>
        <mat-error *ngIf="eventForm.get('text').errors?.maxlength">Footer is too long (max.
          2000)
        </mat-error>
        <mat-hint align="end">{{eventForm.get('text').value.length}} / 2000</mat-hint>
      </mat-form-field>

      <mat-form-field class="full-width-container">
        <mat-label>Footer Text (Premium)</mat-label>
        <textarea matInput formControlName="footerText"></textarea>
        <mat-error *ngIf="eventForm.get('footerText').errors?.maxlength">Footer is too long (max.
          1900)
        </mat-error>
        <mat-hint align="end">{{eventForm.get('footerText').value.length}} / 1900</mat-hint>
      </mat-form-field>

      <mat-form-field class="full-width-container">
        <mat-label>Footer Icon (Premium)</mat-label>
        <input matInput formControlName="footerIcon">
      </mat-form-field>

      <mat-form-field class="full-width-container">
        <mat-label>Embed Image URL (Premium)</mat-label>
        <input matInput formControlName="embedImage">
      </mat-form-field>

      <mat-form-field class="full-width-container">
        <mat-label>Embed Thumbnail URL (Premium)</mat-label>
        <input matInput formControlName="embedThumbnail">
      </mat-form-field>
      </ng-container>

      <!-- Submit -->
      <mat-card-actions class="full-width-container mt-4">
        <button mat-raised-button color="primary" type="submit" class="create-event-button" [disabled]="!eventForm.valid || creationInProgress">
          <mat-icon *ngIf="!creationInProgress">add_circle</mat-icon>
          <span>{{ creationInProgress ? 'Creating...' : 'Create Event' }}</span>
        </button>
        <div *ngIf="creationInProgress" class="loading-indicator">
          <mat-spinner diameter="24"></mat-spinner>
          <span>Creating event...</span>
        </div>
      </mat-card-actions>
      </form>
    </div>

    <!-- Divider -->
    <div class="col-auto d-none d-xxl-flex align-items-stretch px-0">
      <div class="content-divider"></div>
    </div>

    <!-- Preview Column -->
    <div class="col-12 col-xxl-auto preview-column">
      <div class="section-header">
        <h2 class="section-title">Preview</h2>
        <mat-icon class="section-icon">visibility</mat-icon>
      </div>
      <div class="preview-container">
        <app-embed-preview></app-embed-preview>
      </div>
    </div>
  </div>
</div>
